import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import type { Toy } from "../services/api";

export interface OrderItemState {
  toy: Toy;
  quantity: number;
}

interface OrderContextValue {
  items: OrderItemState[];
  totalPoints: number;
  add: (toy: Toy, quantity?: number) => void;
  removeOne: (toyId: string) => void;
  removeAllOf: (toyId: string) => void;
  clear: () => void;
}

const OrderContext = createContext<OrderContextValue | undefined>(undefined);

const STORAGE_KEY = "toddlar_order_items";

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<OrderItemState[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((i: any) => i && i.toy && i.toy._id && i.quantity);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const add = (toy: Toy, quantity: number = 1) => {
    setItems((prev) => {
      const index = prev.findIndex((i) => i.toy._id === toy._id);
      if (index >= 0) {
        const next = [...prev];
        const current = next[index];
        const newQty = Math.min(current.quantity + quantity, toy.stockQuantity);
        next[index] = { ...current, quantity: newQty };
        return next;
      }
      return [
        ...prev,
        { toy, quantity: Math.min(quantity, toy.stockQuantity) },
      ];
    });
  };

  const removeOne = (toyId: string) => {
    setItems((prev) => {
      const index = prev.findIndex((i) => i.toy._id === toyId);
      if (index === -1) return prev;
      const next = [...prev];
      const current = next[index];
      if (current.quantity > 1) {
        next[index] = { ...current, quantity: current.quantity - 1 };
        return next;
      }
      return prev.filter((i) => i.toy._id !== toyId);
    });
  };

  const removeAllOf = (toyId: string) => {
    setItems((prev) => prev.filter((i) => i.toy._id !== toyId));
  };

  const clear = () => setItems([]);

  const totalPoints = useMemo(
    () => items.reduce((sum, i) => sum + i.toy.points * i.quantity, 0),
    [items]
  );

  const value = useMemo<OrderContextValue>(
    () => ({ items, totalPoints, add, removeOne, removeAllOf, clear }),
    [items, totalPoints]
  );

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrder = () => {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be used within OrderProvider");
  return ctx;
};
